/**
 * The "safety triangle" (a.k.a. the Amazon mega-menu aim triangle).
 *
 * The problem it solves: a submenu/popover opened from a hovered item can only be reached by
 * moving the pointer diagonally toward it — and that path crosses the *other* items, each of
 * which fires its own hover and steals the popover. The result is a popover you can see but
 * can never touch. A plain close-delay does not fix it: the delay governs when the popover
 * hides, not which item owns it.
 *
 * The fix: form a triangle from where the pointer just was to the two far corners of the
 * popover's near edge. While the pointer stays inside that triangle it is *travelling toward*
 * the popover, so hovers over intervening items are ignored. Step outside it — pause, or head
 * somewhere else — and the hover takes effect immediately.
 *
 * Kept free of DOM and framework so the geometry can be tested directly; the caller supplies
 * plain rects and points.
 */

export type Point = { x: number; y: number };

/** Only the fields needed from a DOMRect, so tests need not fabricate one. */
export type EdgeRect = { left: number; right: number; top: number; bottom: number };

/**
 * Twice the signed area of the triangle abc. Sign tells which side of ab the point c is on;
 * that is all the containment test needs.
 */
const cross = (a: Point, b: Point, c: Point): number => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);

/**
 * Inclusive point-in-triangle test, orientation-agnostic.
 *
 * A point on an edge counts as inside: the pointer frequently rides exactly along the
 * triangle's boundary when moving straight at the popover, and excluding it there would make
 * the guard flicker.
 */
export const isPointInTriangle = (point: Point, a: Point, b: Point, c: Point): boolean => {
  const d1 = cross(point, a, b);
  const d2 = cross(point, b, c);
  const d3 = cross(point, c, a);

  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;

  // Mixed signs mean the point falls outside at least one edge. All-same-sign (or zero,
  // i.e. on an edge) means inside.
  return !(hasNegative && hasPositive);
};

/**
 * Is the pointer travelling from `from` toward `rect`?
 *
 * The triangle's apex is the previous pointer position and its base is whichever vertical
 * edge of the rect faces that apex — so this works whether the popover opened to the left or
 * the right, which matters because DevExtreme flips a popover's side when it runs out of room.
 *
 * Returns false when the pointer is already inside the rect (nothing to protect) or when the
 * two points coincide (no movement to infer direction from).
 */
export const isAimingAt = (from: Point, to: Point, rect: EdgeRect): boolean => {
  if (from.x === to.x && from.y === to.y) {
    return false;
  }

  const isInsideRect = to.x >= rect.left && to.x <= rect.right && to.y >= rect.top && to.y <= rect.bottom;
  if (isInsideRect) {
    return false;
  }

  // Face the edge nearest the pointer's origin. Equidistant/overlapping horizontally is
  // treated as "no corridor" — a vertical approach has no triangle to speak of.
  const facingEdgeX = rect.right <= from.x ? rect.right : rect.left <= from.x ? null : rect.left;
  if (facingEdgeX === null) {
    return false;
  }

  return isPointInTriangle(to, from, { x: facingEdgeX, y: rect.top }, { x: facingEdgeX, y: rect.bottom });
};
